import { ListeningClozeResult, ListeningDiffChunk, ListeningSentenceResult } from './types';

const STOPWORDS = new Set([
  'a',
  'an',
  'the',
  'and',
  'or',
  'but',
  'to',
  'for',
  'of',
  'in',
  'on',
  'at',
  'with',
  'as',
  'by',
  'is',
  'are',
  'was',
  'were',
  'be',
  'am',
  'do',
  'did',
  'does',
  'have',
  'has',
  'had',
]);

const IRREGULAR_VARIANTS: Record<string, string[]> = {
  go: ['goes', 'went', 'gone', 'going'],
  do: ['does', 'doing', 'done', 'did'],
  be: ['am', 'is', 'are', 'was', 'were', 'been', 'being'],
  have: ['has', 'having', 'had'],
  child: ['children'],
  man: ['men'],
  woman: ['women'],
  mouse: ['mice'],
};

// 美英拼写差异映射表
const US_UK_SPELLING: Record<string, string[]> = {
  // 美式 -> 英式
  color: ['colour'],
  honor: ['honour'],
  favor: ['favour'],
  behavior: ['behaviour'],
  organize: ['organise'],
  realize: ['realise'],
  analyze: ['analyse'],
  center: ['centre'],
  theater: ['theatre'],
  liter: ['litre'],
  meter: ['metre'],
  program: ['programme'],
  catalog: ['catalogue'],
  dialog: ['dialogue'],
  traveled: ['travelled'],
  traveling: ['travelling'],
  counselor: ['counsellor'],
  jewelry: ['jewellery'],
  license: ['licence'],
  defense: ['defence'],
  offense: ['offence'],
  practice: ['practise'],
  // 英式 -> 美式（双向支持）
  colour: ['color'],
  honour: ['honor'],
  favour: ['favor'],
  behaviour: ['behavior'],
  organise: ['organize'],
  realise: ['realize'],
  analyse: ['analyze'],
  centre: ['center'],
  theatre: ['theater'],
  litre: ['liter'],
  metre: ['meter'],
  programme: ['program'],
  catalogue: ['catalog'],
  dialogue: ['dialog'],
  travelled: ['traveled'],
  travelling: ['traveling'],
  counsellor: ['counselor'],
  jewellery: ['jewelry'],
  licence: ['license'],
  defence: ['defense'],
  offence: ['offense'],
  practise: ['practice'],
};

// 常见同音词（谨慎使用，仅限确实容易混淆的）
const COMMON_HOMOPHONES: Record<string, string[]> = {
  their: ['there'],
  there: ['their'],
  to: ['too'],
  too: ['to'],
  by: ['buy'],
  buy: ['by'],
  // 注意：不包含they're/your等复杂形式，避免过度容错
};

const normalizeWord = (value: string) => value.replace(/[^a-z0-9']+/gi, '').toLowerCase();

// 词形归一化的tokenize函数
const tokenizeWithNormalization = (value: string) =>
  value
    .split(/\s+/)
    .map((token) => ({ 
      raw: token.trim(), 
      norm: normalizeWord(token),
      // 添加词形归一化版本，用于更智能的匹配
      lemma: getLemma(token)
    }))
    .filter((token) => token.norm);

// 获取词根（简单的词形归一化）
const getLemma = (word: string): string => {
  const normalized = normalizeWord(word);
  if (!normalized) return '';
  
  // 移除常见的词形变化后缀
  if (normalized.endsWith('ing') && normalized.length > 5) return normalized.slice(0, -3);
  if (normalized.endsWith('ed') && normalized.length > 4) return normalized.slice(0, -2);
  if (normalized.endsWith('s') && normalized.length > 3) return normalized.slice(0, -1);
  if (normalized.endsWith('ies') && normalized.length > 5) return normalized.slice(0, -3) + 'y';
  
  return normalized;
};

// 检查是否为停用词
const isStopWord = (word: string): boolean => {
  return STOPWORDS.has(normalizeWord(word));
};

// 合并相邻的同类停用词错误
const mergeAdjacentStopwordErrors = (diff: ListeningDiffChunk[]): ListeningDiffChunk[] => {
  const merged: ListeningDiffChunk[] = [];
  let currentGroup: ListeningDiffChunk[] = [];
  
  for (const chunk of diff) {
    const isStopWordError = chunk.kind !== 'hit' && 
      (chunk.expected ? isStopWord(chunk.expected) : true) && 
      (chunk.actual ? isStopWord(chunk.actual) : true);
    
    if (isStopWordError && currentGroup.length > 0 && 
        currentGroup[currentGroup.length - 1].kind === chunk.kind) {
      // 同类相邻停用词错误，合并到当前组
      currentGroup.push(chunk);
    } else {
      // 新的错误类型或非停用词错误，处理之前组并开始新组
      if (currentGroup.length > 0) {
        merged.push(...collapseStopwordGroup(currentGroup));
        currentGroup = [];
      }
      
      if (isStopWordError) {
        currentGroup.push(chunk);
      } else {
        merged.push(chunk);
      }
    }
  }
  
  // 处理最后一组
  if (currentGroup.length > 0) {
    merged.push(...collapseStopwordGroup(currentGroup));
  }
  
  return merged;
};

// 将停用词错误组折叠为单个错误（降低权重）
const collapseStopwordGroup = (group: ListeningDiffChunk[]): ListeningDiffChunk[] => {
  if (group.length === 0) return [];
  
  // 如果是单个停用词错误，直接返回
  if (group.length === 1) return group;
  
  // 多个相邻停用词错误，合并为单个，标记为minor-spell
  const first = group[0];
  const collapsed: ListeningDiffChunk = {
    kind: 'substitution', // 用substitution表示轻微错误
    expected: first.kind === 'extra' ? undefined : first.expected,
    actual: first.kind === 'missing' ? undefined : first.actual,
  };
  
  return [collapsed];
};

// 加权计算正确率
const calculateWeightedAccuracy = (diff: ListeningDiffChunk[], totalTokens: number): number => {
  let weightedErrors = 0;
  let totalWeight = 0;
  
  for (const chunk of diff) {
    const expectedWord = chunk.expected ?? chunk.actual ?? '';
    const isStop = isStopWord(expectedWord);
    const weight = isStop ? 0.3 : 1.0; // 停用词权重0.3，内容词权重1.0
    
    totalWeight += weight;
    if (chunk.kind !== 'hit') {
      weightedErrors += weight;
    }
  }
  
  const accuracy = totalWeight > 0 ? Math.round(((totalWeight - weightedErrors) / totalWeight) * 100) : 0;
  return accuracy;
};

// 原有的tokenize函数（保持向后兼容）
const tokenize = (value: string) =>
  value
    .split(/\s+/)
    .map((token) => ({ raw: token.trim(), norm: normalizeWord(token) }))
    .filter((token) => token.norm);

const buildEditTable = (ref: string[], ans: string[]) => {
  const rows = ref.length + 1;
  const cols = ans.length + 1;
  const table: number[][] = Array.from({ length: rows }, () => Array(cols).fill(0));

  for (let i = 0; i < rows; i++) table[i][0] = i;
  for (let j = 0; j < cols; j++) table[0][j] = j;

  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = ref[i - 1] === ans[j - 1] ? 0 : 1;
      table[i][j] = Math.min(table[i - 1][j] + 1, table[i][j - 1] + 1, table[i - 1][j - 1] + cost);
    }
  }

  return table;
};

const backtrackDiff = (refTokens: { raw: string; norm: string }[], ansTokens: { raw: string; norm: string }[]) => {
  const ref = refTokens.map((t) => t.norm);
  const ans = ansTokens.map((t) => t.norm);
  const table = buildEditTable(ref, ans);
  const chunks: ListeningDiffChunk[] = [];

  let i = ref.length;
  let j = ans.length;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && table[i][j] === table[i - 1][j - 1] && ref[i - 1] === ans[j - 1]) {
      chunks.push({ kind: 'hit', expected: refTokens[i - 1].raw });
      i -= 1;
      j -= 1;
    } else if (i > 0 && j > 0 && table[i][j] === table[i - 1][j - 1] + 1) {
      chunks.push({
        kind: 'substitution',
        expected: refTokens[i - 1].raw,
        actual: ansTokens[j - 1].raw,
      });
      i -= 1;
      j -= 1;
    } else if (i > 0 && table[i][j] === table[i - 1][j] + 1) {
      chunks.push({ kind: 'missing', expected: refTokens[i - 1].raw });
      i -= 1;
    } else if (j > 0 && table[i][j] === table[i][j - 1] + 1) {
      chunks.push({ kind: 'extra', actual: ansTokens[j - 1].raw });
      j -= 1;
    } else {
      // fallback to avoid infinite loops
      break;
    }
  }

  return chunks.reverse();
};

const summarizeDiff = (diff: ListeningDiffChunk[]) => {
  const missing: string[] = [];
  const substitutions: Array<{ expected: string; actual: string }> = [];
  const extra: string[] = [];

  for (const chunk of diff) {
    if (chunk.kind === 'missing' && chunk.expected) missing.push(chunk.expected);
    if (chunk.kind === 'extra' && chunk.actual) extra.push(chunk.actual);
    if (chunk.kind === 'substitution' && chunk.expected && chunk.actual) {
      substitutions.push({ expected: chunk.expected, actual: chunk.actual });
    }
  }

  return { missing, substitutions, extra };
};

const diffSummaryText = (summary: ReturnType<typeof summarizeDiff>) => {
  const parts: string[] = [];
  if (summary.missing.length) parts.push(`missing: ${summary.missing.slice(0, 4).join(', ')}`);
  if (summary.substitutions.length)
    parts.push(
      `subs: ${summary.substitutions
        .slice(0, 3)
        .map((pair) => `${pair.expected}->${pair.actual}`)
        .join('; ')}`
    );
  if (summary.extra.length) parts.push(`extra: ${summary.extra.slice(0, 3).join(', ')}`);
  return parts.join(' | ');
};

// 移除说话者标识（如 "Ed: ..."）
// 只匹配句子开头的 "名字:" 格式，用户听音频不知道说话者名字
const removeSpeakerLabels = (text: string): string => {
  // 只匹配句子开头：名字后跟冒号和空格
  return text.replace(/^[A-Z][a-z]+:\s*/gm, '');
};

export function scoreListeningSentence(transcript: string, answer: string): ListeningSentenceResult {
  // 移除说话者标识后再评分
  const cleanTranscript = removeSpeakerLabels(transcript);
  const refTokens = tokenize(cleanTranscript);
  const ansTokens = tokenize(answer);
  const rawDiff = backtrackDiff(refTokens, ansTokens);

  // 1. 合并相邻的同类停用词错误
  const processedDiff = mergeAdjacentStopwordErrors(rawDiff);
  
  // 2. 使用加权计算正确率
  const totalTokens = refTokens.length;
  const accuracy = calculateWeightedAccuracy(processedDiff, totalTokens);

  const summary = summarizeDiff(processedDiff);
  const diffSummary = diffSummaryText(summary);

  return {
    mode: 'sentence',
    transcript,
    userAnswer: answer,
    accuracy,
    correctTokens: processedDiff.filter((chunk) => chunk.kind === 'hit').length,
    totalTokens,
    diff: processedDiff,
    summary,
    diffSummary,
    evaluatedAt: Date.now(),
  };
}

const normalizeAnswer = (value: string) => normalizeWord(value.trim());

const capFirst = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const morphologicalVariants = (base: string): string[] => {
  const variants = new Set<string>();
  const lower = normalizeWord(base);
  if (!lower) return [];
  variants.add(lower);
  variants.add(capFirst(lower));

  if (lower.endsWith('es') && lower.length > 4) {
    variants.add(lower.slice(0, -2));
  }
  if (lower.endsWith('s') && lower.length > 3) {
    variants.add(lower.slice(0, -1));
  }

  const irregular = IRREGULAR_VARIANTS[lower];
  if (irregular) irregular.forEach((form) => variants.add(form));

  const add = (form: string) => variants.add(form);
  add(`${lower}s`);
  if (lower.endsWith('y')) add(`${lower.slice(0, -1)}ies`);
  add(`${lower}ed`);
  add(`${lower}ing`);
  add(`${lower}er`);
  add(`${lower}est`);

  return Array.from(variants);
};

export const buildVariantSet = (base: string, options?: { includeHomophones?: boolean }) => {
  const variants = new Set<string>();
  const normalizedBase = normalizeWord(base);
  
  // 1. 基础词形变化
  morphologicalVariants(base).forEach((v) => variants.add(normalizeAnswer(v)));
  
  // 2. 不规则动词变体
  const irregular = IRREGULAR_VARIANTS[normalizedBase];
  irregular?.forEach((v) => variants.add(normalizeAnswer(v)));
  for (const [root, forms] of Object.entries(IRREGULAR_VARIANTS)) {
    if (forms.includes(normalizedBase)) {
      variants.add(normalizeAnswer(root));
    }
  }
  
  // 3. 美英拼写差异
  const usUk = US_UK_SPELLING[normalizedBase];
  usUk?.forEach((v) => variants.add(normalizeAnswer(v)));
  for (const [variant, forms] of Object.entries(US_UK_SPELLING)) {
    if (forms.includes(normalizedBase)) {
      variants.add(normalizeAnswer(variant));
    }
  }
  
  // 4. 同音词（可选，谨慎使用）
  if (options?.includeHomophones) {
    const homophones = COMMON_HOMOPHONES[normalizedBase];
    homophones?.forEach((v) => variants.add(normalizeAnswer(v)));
    for (const [variant, forms] of Object.entries(COMMON_HOMOPHONES)) {
      if (forms.includes(normalizedBase)) {
        variants.add(normalizeAnswer(variant));
      }
    }
  }
  
  return variants;
};

const deriveClozeIndexes = (template: string, indexes?: number[]): number[] => {
  const tokens = tokenize(template).map((t) => t.raw);
  const sanitized = (indexes ?? []).filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < tokens.length);
  if (sanitized.length) return Array.from(new Set(sanitized)).sort((a, b) => a - b);

  const candidates = tokens
    .map((token, index) => ({ token: normalizeWord(token), index }))
    .filter((item) => item.token.length >= 4 && !STOPWORDS.has(item.token));

  return candidates
    .sort((a, b) => b.token.length - a.token.length)
    .slice(0, Math.min(3, candidates.length))
    .map((item) => item.index)
    .sort((a, b) => a - b);
};

export const buildListeningCloze = (template: string, clozeIndexes?: number[]) => {
  const tokens = template.split(/\s+/);
  const indexes = deriveClozeIndexes(template, clozeIndexes);
  const answers: string[] = [];

  indexes.forEach((tokenIndex, slotIndex) => {
    const raw = tokens[tokenIndex];
    if (typeof raw === 'string') {
      answers.push(raw);
      tokens[tokenIndex] = `{{blank_${slotIndex}}}`;
    }
  });

  return { template: tokens.join(' '), answers, indexes };
};

export const scoreListeningCloze = (
  template: string,
  answers: string[],
  inputs: string[],
  options?: { includeHomophones?: boolean }
): ListeningClozeResult => {
  const blanks = answers.map((expected, index) => {
    const accepted = Array.from(buildVariantSet(expected, options));
    const normalizedAnswer = normalizeAnswer(inputs[index] ?? '');
    const correct = accepted.includes(normalizedAnswer);
    return {
      index,
      expected,
      answer: inputs[index] ?? '',
      correct,
      accepted,
    };
  });

  const correctCount = blanks.filter((item) => item.correct).length;
  const accuracy = answers.length ? Math.round((correctCount / answers.length) * 100) : 0;

  return {
    mode: 'cloze',
    template,
    blanks,
    accuracy,
    evaluatedAt: Date.now(),
    rawAnswer: inputs,
  };
};
